import { Injectable, WritableSignal, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, map, of, shareReplay, catchError } from 'rxjs';
import * as Sentry from '@sentry/angular';

import { environment } from '@environments/environment';
import { serverEnvironment } from '@environments/environment.server';
import { Globals } from '@core/globals';
import { GlobalsService } from '@services/globals.service';
import { SsrService } from '@services/ssr.service';
import { CarAAAIdResponse, CarDataResponse, CarEquipment, CarEquipmentFinal, CarItem, CarPaymentOptions } from '@models/car.model';

@Injectable({
  providedIn: 'root',
})
export class CarService {
  private http = inject(HttpClient);
  private globals = inject(Globals);
  private globalsService = inject(GlobalsService);
  private ssrService = inject(SsrService);
  public car: WritableSignal<CarItem | undefined> = signal(undefined);
  public loaded: WritableSignal<boolean> = signal(false);

  getCarData(id: number): Observable<CarItem | undefined> {
    if (!id) return of(undefined);
    if (this.car()?.id === id) return of(this.car());
    return this.http.post<CarDataResponse>(`${this.ssrService.isServer() ? serverEnvironment.carsApi : environment.carsApi}?withEquipCat=1&withDetails=1`, { id: id, leasingNumberOfMonth: 60 }, { headers: this.globalsService.headerHash() }).pipe(
      shareReplay(1),
      map((response) => response.message.items[0]),
      map((response) => {
        this.loaded.set(true);
        if (!response) return;

        response.paymentOptions = this.calculatePaymentOptions(response.paymentOptions, response.price.cash);

        response.gallery = [];
        if (response.photos?.default === null) response.photos.default = [{ type: 4, url: ['https://static.aaaauto.eu/img/nophoto/nophoto-big.jpg'] }];
        if (response.photos?.default[0]?.url) {
          for (let photo of response.photos.default?.[0].url) {
            photo = this.globalsService.replacePlaceholderPhoto(photo, '1600x1200x100') + '?' + this.globals.photoCacheVersion;
            const src: string = photo.replace(/\d+x\d+x\d+/, '1600x1200x100');
            const thumb: string = photo.replace(/\d+x\d+x\d+/, '384x289x100');
            const thumbLarge: string = photo.replace(/\d+x\d+x\d+/, '950x712x100');
            const placeholder: string = photo.replace(/\d+x\d+x\d+/, '40x30x100');
            response.gallery.push({ type: 'my-image-template', data: { src, thumb, thumbLarge, placeholder, alt: response.make.title + ' ' + response.model.title + ' - ' + response.productionYear } });
          }
        }

        const equipment = this.mapEquipment(response.equipment);
        response.equipmentFinal = equipment.equipment;
        response.equipmentVisible = equipment.visible;

        response.evWebModules = {};
        response.evWebModules.stats = this.isEvStatsModuleEnabled(response);
        this.car.set(response);
        return response;
      }),
      catchError((error) => {
        this.loaded.set(true);
        console.error('Error loading car data:', error);
        Sentry.captureMessage('Nepodařilo se načíst data vozidla - Error: ' + error.message, {
          level: 'error',
          extra: {
            carId: id,
            error: error,
          },
        });
        return of(undefined);
      }),
    );
  }

  isEvStatsModuleEnabled(car: CarItem): boolean {
    if (car.fuel?.id != 7) return false;
    if (car.evProperties.ac1x16AchargingTimeH && car.evProperties.ac1x16AchargingTimeH > 0) return false;
    if (car.evProperties.ac3x16AchargingTimeH && car.evProperties.ac3x16AchargingTimeH > 0) return false;
    if (car.evProperties.ac3x32AchargingTimeH && car.evProperties.ac3x32AchargingTimeH > 0) return false;
    return true;
  }

  mapEquipment(equipment: CarEquipment): { equipment: CarEquipmentFinal; visible: boolean } {
    const categories: { [key: string]: string[] } = {
      exterior: ['AH', 'AHB', 'BIXEN', 'PB', 'ED', 'CCB', 'LASL', 'FLED', 'MLED', 'TB', 'HAS', 'ASUS', 'XE'],
      interior: ['ALS', 'AAC', 'ASA', '1/2 LS', 'DG', 'ES', 'PRS', 'AC', 'LS', 'MS', 'HEAT', 'ALH', 'VS', 'SAM', 'PAN', 'PA', 'SRW', 'SSEATS', 'SR', 'HVAC', 'HS', 'HRS', 'HFW', 'HSW'],
      technology: ['CAM_360', 'ACC', 'ANDROID', 'CARPLAY', 'LDS', 'TRAVELAS', 'APARK', 'APLT', 'WICH', 'FREESCH', 'HUD', 'MIR', 'NAVIG', 'NV', 'EPEDAL', 'RCAM', 'ENHAPLT', 'FSDCP', 'LCS', 'CC', 'VREGEN', 'VICO', 'DMS'],
      ev: ['FREESCH', 'APLT', 'ENHAPLT', 'FSDCP', 'FRUNK', 'HVAC', 'VREGEN', 'EPEDAL'],
    };

    const customEquipment: CarEquipmentFinal = {
      exterior: { items: [], itemsSplit: 0 },
      interior: { items: [], itemsSplit: 0 },
      technology: { items: [], itemsSplit: 0 },
      ev: { items: [], itemsSplit: 0 },
    };

    const items = [...(equipment.exterior.items || []), ...(equipment.extra.items || []), ...(equipment.infotainment.items || []), ...(equipment.interior.items || []), ...(equipment.other.items || []), ...(equipment.safety.items || [])];

    let visible = false;
    items.forEach((equip) => {
      Object.entries(categories).forEach(([category, ids]) => {
        if (ids.includes(equip.id)) {
          visible = true;
          customEquipment[category as keyof CarEquipmentFinal].items.push(equip);
        }
      });
    });

    Object.keys(customEquipment).forEach((key) => {
      const itemsLength = customEquipment[key as keyof CarEquipmentFinal].items.length;
      const itemsSplit = Math.max(equipment.maxItems, Math.min(itemsLength, Math.round(itemsLength / 2)));
      customEquipment[key as keyof CarEquipmentFinal].itemsSplit = itemsSplit;
    });

    return { equipment: customEquipment, visible: visible };
  }

  getCarDataByAaaid(aaaid: number): Observable<CarAAAIdResponse | undefined> {
    return this.http.post<any>(`${this.ssrService.isServer() ? serverEnvironment.carsApi : environment.carsApi}?withEquipCat=1&withDetails=1`, { aaaid: aaaid }, { headers: this.globalsService.headerHash() }).pipe(
      catchError((error) => {
        console.error('Error loading car data by aaaid:', error);
        Sentry.captureMessage('Nepodařilo se načíst data vozidla podle aaaid - Error: ' + error.message, {
          level: 'error',
          extra: {
            aaaid: aaaid,
            error: error,
          },
        });
        return of(undefined);
      }),
    );
  }

  calculatePaymentOptions(paymentOptions: CarPaymentOptions[], price: number): CarPaymentOptions[] {
    if (!paymentOptions || paymentOptions?.length === 0) {
      const months = [12, 24, 36, 48, 60, 72, 84, 96, 108, 120];
      const advance = [70, 60, 50, 40, 30, 20, 10, 0];
      paymentOptions = [];
      for (let item of advance) {
        const paymentPlans = [];
        for (let month of months) {
          const advancePrice = (price / 100) * item;
          paymentPlans.push({
            advancePrice: advancePrice,
            numberOfMonths: month,
          });
        }
        const paymentOptionsItem = {
          advance: item,
          paymentPlans: [...paymentPlans],
        };
        paymentOptions.push(paymentOptionsItem);
      }
      return paymentOptions;
    } else {
      return paymentOptions;
    }
  }
}
